import { doc, getDoc, updateDoc, documentId, getDocs, query, where, orderBy, limit, startAfter } from "firebase/firestore";
import { getAuth } from "firebase/auth";
import { deleteRecordAndFiles, permissionsDB, projectsSchemeDB, updateRecord, userCollectionsDB, uploadFileToGCloud } from "../../libs/firebase";
import { generateID } from "ui";
import { CollectionInterface, CollectionUserImages, CollectionUserMaterials, validateCollection } from "ts-interfaces";
import { firebaseHelper } from "ui";
import { ProjectSchemeInterface, Permissions } from "ts-interfaces";
import { hasAccessToResource } from "ui/utils/usePermissions";
import { produce } from 'immer'

// 🔧 SIMPLIFIED COLLECTION FETCHING FUNCTIONS

/**
 * Fetch collections the user owns directly (no permissions needed)
 */
export async function getOwnedCollections(
  userId: string, 
  currentCollections: CollectionInterface[] = [], 
  limitRecords: number = 20
): Promise<CollectionInterface[]> {
  if (!userCollectionsDB || !userId) return [];
  
  try {
    let q;
    
    if (currentCollections.length > 0) {
      q = query(
        userCollectionsDB,
        where("uid", "==", userId),
        orderBy("updatedAt", "desc"),
        startAfter(currentCollections[currentCollections.length - 1].updatedAt),
        limit(limitRecords)
      );
    } else {
      q = query(
        userCollectionsDB,
        where("uid", "==", userId),
        orderBy("updatedAt", "desc"),
        limit(limitRecords)
      );
    }
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => doc.data() as CollectionInterface);
  } catch (error) {
    console.error('Error fetching owned collections:', error);
    return [];
  }
}

/**
 * Fetch collections the user has read access to (via permissions.collectionRead)
 */
export async function getReadOnlyCollections(
  permissions: Permissions | null,
  currentCollections: CollectionInterface[] = [],
  limitRecords: number = 20
): Promise<CollectionInterface[]> {
  if (!userCollectionsDB || !permissions?.collectionRead?.length) return [];
  
  try {
    const collectionReadIds = permissions.collectionRead;
    
    // Filter out collections we already have for pagination
    const existingIds = currentCollections.map(c => c.id);
    const idsToFetch = collectionReadIds.filter(id => !existingIds.includes(id));
    
    if (idsToFetch.length === 0) return [];
    
    // Apply limit to how many we fetch
    const limitedIds = idsToFetch.slice(0, limitRecords);
    
    return await fetchCollectionsByIds(limitedIds);
  } catch (error) {
    console.error('Error fetching read-only collections:', error);
    return [];
  }
}

/**
 * Fetch collections the user has write access to (via permissions.collectionWrite)
 */
export async function getWritableSharedCollections(
  permissions: Permissions | null,
  currentCollections: CollectionInterface[] = [],
  limitRecords: number = 20
): Promise<CollectionInterface[]> {
  if (!userCollectionsDB || !permissions?.collectionWrite?.length) return [];
  
  try {
    const collectionWriteIds = permissions.collectionWrite;
    
    // Filter out collections we already have for pagination
    const existingIds = currentCollections.map(c => c.id);
    const idsToFetch = collectionWriteIds.filter(id => !existingIds.includes(id));
    
    if (idsToFetch.length === 0) return [];
    
    // Apply limit to how many we fetch
    const limitedIds = idsToFetch.slice(0, limitRecords);

    
    return await fetchCollectionsByIds(limitedIds);
  } catch (error) {
    console.error('Error fetching writable shared collections:', error);
    return [];
  }
}

/**
 * Fetch collections from projects the user has read access to
 */
export async function getProjectReadCollections(
  permissions: Permissions | null,
  currentCollections: CollectionInterface[] = [],
  limitRecords: number = 20
): Promise<CollectionInterface[]> {
  if (!userCollectionsDB || !permissions?.projectRead?.length) return [];
  
  try {
    const projectReadIds = permissions.projectRead;
    return await fetchCollectionsByProjectIds(projectReadIds, currentCollections, limitRecords);
  } catch (error) {
    console.error('Error fetching project read collections:', error);
    return [];
  }
}

/**
 * Fetch collections from projects the user has write access to
 */
export async function getProjectWriteCollections(
  permissions: Permissions | null,
  currentCollections: CollectionInterface[] = [],
  limitRecords: number = 20
): Promise<CollectionInterface[]> {
  if (!userCollectionsDB || !permissions?.projectWrite?.length) return [];
  
  try {
    const projectWriteIds = permissions.projectWrite;
    return await fetchCollectionsByProjectIds(projectWriteIds, currentCollections, limitRecords);
  } catch (error) {
    console.error('Error fetching project write collections:', error);
    return [];
  }
}

export async function getUserProjetsCollections(
  user_id: string,
  currentCollections: CollectionInterface[] = [],
  limitRecords: number = 20
): Promise<CollectionInterface[]> {
  if (!projectsSchemeDB || !user_id) return [];

  try {
    // First, get ALL projects owned by the user (we'll filter in JavaScript)
    // We can't use Firestore to query for "archive == false OR archive doesn't exist"
    const q = query(
      projectsSchemeDB,
      where("owner", "==", user_id),
      orderBy("updatedAt", "desc")
    );

    const snapshot = await getDocs(q);
    const allProjects = snapshot.docs.map(doc => doc.data() as ProjectSchemeInterface);
    
    // Filter projects where archive is false or doesn't exist (undefined)
    const activeProjects = allProjects.filter(project => 
      project.archive === false || project.archive === undefined
    );

    
    // Apply limit to the filtered results
    const limitedProjects = activeProjects.slice(0, limitRecords);
    
    // Extract project IDs
    const projectIds = limitedProjects.map(project => project.id).filter(Boolean);

    
    if (projectIds.length === 0) return [];
    
    // Fetch collections for these projects using the existing helper
    return await fetchCollectionsByProjectIds(projectIds, currentCollections, limitRecords);
    
  } catch (error) {
    console.error('Error fetching user project collections:', error);
    return [];
  }
}

// Helper function to fetch collections by their IDs
async function fetchCollectionsByIds(collectionIds: string[]): Promise<CollectionInterface[]> {
  if (!userCollectionsDB || collectionIds.length === 0) return [];
  
  const allCollections: CollectionInterface[] = [];
  const batchSize = 10; // Firestore 'in' query limit
  
  for (let i = 0; i < collectionIds.length; i += batchSize) {
    const batch = collectionIds.slice(i, i + batchSize);
    
    const q = query(
      userCollectionsDB,
      where(documentId(), "in", batch),
      orderBy("updatedAt", "desc")
    );
    
    const snapshot = await getDocs(q);
    const batchCollections = snapshot.docs.map(doc => doc.data() as CollectionInterface);
    allCollections.push(...batchCollections);
  }
  
  return allCollections.sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime());
}

// Helper function to fetch collections by project IDs
async function fetchCollectionsByProjectIds(
  projectIds: string[], 
  currentCollections: CollectionInterface[] = [],
  limitRecords: number = 20
): Promise<CollectionInterface[]> {
  if (!userCollectionsDB || projectIds.length === 0) return [];
  
  const allProjectCollections: CollectionInterface[] = [];
  const batchSize = 10; // Firestore 'in' query limit
  
  for (let i = 0; i < projectIds.length; i += batchSize) {
    const batch = projectIds.slice(i, i + batchSize);
    
    const q = query(
      userCollectionsDB,
      where("associatedProjectId", "in", batch),
      orderBy("updatedAt", "desc")
    );
    
    const snapshot = await getDocs(q);
    const batchCollections = snapshot.docs.map(doc => doc.data() as CollectionInterface);
    allProjectCollections.push(...batchCollections);
  }
  
  // Remove duplicates and collections we already have
  const existingIds = currentCollections.map(c => c.id);
  const newCollections = allProjectCollections.filter(c => !existingIds.includes(c.id));
  
  return newCollections
    .sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime())
    .slice(0, limitRecords);
}


export async function duplicateCollectionScheme(collection: CollectionInterface, permissions: Permissions | null): Promise<CollectionInterface> {
    const auth = getAuth();
      const user = auth.currentUser;
      if (!user) throw new Error("User not authenticated");

      const hasWritePermission = hasAccessToResource(permissions, collection.id, 'write', 'collection', collection.uid || '', collection.uid || '');
    if (!hasWritePermission) {
      throw new Error("You do not have permission to duplicate this collection")
    }
    
      const duplicatedCollection = {
        ...collection,
        name: `Copy of ${collection.name}`,
        id: generateID(),
        createdAt: Date.now(),
        updatedAt: Date.now(),
        // Reset these fields for the duplicate
        associatedBoardUrl: "",
        associatedProjectId: "",
        writeAccess: [], // Empty array instead of inheriting from original
        uid: user.uid, // Set to the current user creating the duplicate
      };
    
      try {
        await updateRecord(duplicatedCollection.id, "usersMyCollection", duplicatedCollection);
      } catch (error) {
        console.error("Error updating project:", error); // Log the error
        throw new Error("Failed to update project"); // Optionally rethrow the error with a custom message
      }
      return duplicatedCollection;
}

export async function deleteCollection(collection: CollectionInterface, permissions: Permissions | null) {
  try {
    if (!collection?.id) {
      throw new Error("Collection ID is required for deletion")
    }
  
    const hasWritePermission = hasAccessToResource(permissions, collection.id, 'write', 'collection', collection.uid || '', collection.uid || '');
    if (!hasWritePermission) {
      throw new Error("You do not have permission to delete this collection")
    }

    await deleteRecordAndFiles(collection.id, "usersMyCollection")
    return collection
  } catch (error) {
    console.error("Error deleting collection:", error)
    throw new Error(error.message || "Failed to delete collection from database")
  }
}

export async function getSharedCollections(
  permissions: Permissions | null
): Promise<CollectionInterface[]> {
  if (!userCollectionsDB || !permissions) return [];
  
  try {
    // Get shared collection IDs from permissions
    const sharedCollectionIds = [...new Set([
      ...(permissions.collectionRead || []), 
      ...(permissions.collectionWrite || [])
    ])];
    
    if (sharedCollectionIds.length === 0) {
      return [];
    }
    
    const allSharedCollections: CollectionInterface[] = [];
    
    // Fetch shared collections in batches of 10 (Firestore limit)
    for (let i = 0; i < sharedCollectionIds.length; i += 10) {
      const batch = sharedCollectionIds.slice(i, i + 10);
      const batchQuery = query(
        userCollectionsDB,
        where(documentId(), "in", batch)
      );
      
      const batchSnapshot = await getDocs(batchQuery);
      const batchCollections = batchSnapshot.docs.map(doc => doc.data() as CollectionInterface);
      allSharedCollections.push(...batchCollections);
    }
    
    return allSharedCollections;
  } catch (error) {
    console.error('Error fetching shared collections:', error);
    return [];
  }
}

export async function associateCollectionWithProject(
  collectionId: string, 
  projectId: string | null, 
  user_id: string, 
  permissions: Permissions | null
): Promise<void> {
  const docSnap = await getCollectionIfAccessible(collectionId, user_id, permissions);
  if (!docSnap) {
    throw new Error("No access to the collection");
  }

  const currentCollectionData = docSnap.data() as CollectionInterface;
  const currentWriteAccess = currentCollectionData.writeAccess || [];
  
  let updatedWriteAccess = [...currentWriteAccess];

  // If projectId is provided, verify user has access to the project
  if (projectId) {
    const projectDoc = await getDoc(doc(firebaseHelper.db, "projectsScheme", projectId));
    if (!projectDoc.exists()) {
      throw new Error("Project not found");
    }
    
    const projectData = projectDoc.data() as ProjectSchemeInterface;
    const hasProjectAccess = hasAccessToResource(permissions, projectId, 'write', 'project', projectData.owner || '', user_id);

    if (!hasProjectAccess) {
      throw new Error("No access to the project");
    }

    // Get ALL users who have write access to this project
    const projectUsers = await getAllUsersWithProjectWriteAccess(firebaseHelper.db, projectId);
    
    // Add all project users to writeAccess (avoiding duplicates)
    const writeAccessSet = new Set(updatedWriteAccess);
    projectUsers.forEach(userId => writeAccessSet.add(userId));
    updatedWriteAccess = Array.from(writeAccessSet);
  }
  // If removing from project (projectId is null), remove project users but keep direct board users
  else if (currentCollectionData.associatedProjectId) {
    // Get users who had access via the old project
    const oldProjectUsers = await getAllUsersWithProjectWriteAccess(
      firebaseHelper.db, 
      currentCollectionData.associatedProjectId
    );
    
    // Get users who have direct board access
    const directBoardUsers = await getUsersWithDirectCollectionAccess(firebaseHelper.db, collectionId);
    const directBoardUsersSet = new Set(directBoardUsers);
    
    // Remove old project users who don't have direct board access
    updatedWriteAccess = updatedWriteAccess.filter(userId => 
      !oldProjectUsers.includes(userId) || directBoardUsersSet.has(userId)
    );
  }

  // Update the collection
  await updateDoc(docSnap.ref, {
    associatedProjectId: projectId,
    writeAccess: updatedWriteAccess,
    updatedAt: Date.now()
  });
}

// Bulk association function for multiple collections
export async function associateMultipleCollectionsWithProject(
  collectionsToAdd: string[], 
  collectionsToRemove: string[], 
  projectId: string | null, 
  user_id: string, 
  permissions: Permissions | null
): Promise<void> {
  // Validate project access first if projectId is provided
  if (projectId) {
    const projectDoc = await getDoc(doc(firebaseHelper.db, "projectsScheme", projectId));
    if (!projectDoc.exists()) {
      throw new Error("Project not found");
    }
    
    const projectData = projectDoc.data() as ProjectSchemeInterface;
    const hasProjectAccess = hasAccessToResource(permissions, projectId, 'write', 'project', projectData.owner || '', user_id);

    if (!hasProjectAccess) {
      throw new Error("No access to the project");
    }
  }

  // Process all collections in parallel
  const allCollectionIds = [...collectionsToAdd, ...collectionsToRemove];
  const collectionPromises = allCollectionIds.map(async (collectionId) => {
    try {
      if (collectionsToAdd.includes(collectionId)) {
        // Associate with project
        await associateCollectionWithProject(collectionId, projectId, user_id, permissions);
      } else {
        // Remove from project (set to null)
        await associateCollectionWithProject(collectionId, null, user_id, permissions);
      }
    } catch (error) {
      console.error(`Error updating collection ${collectionId}:`, error);
      throw error;
    }
  });

  await Promise.all(collectionPromises);
}


// Helper function to get collection if user has access to it
async function getCollectionIfAccessible(collectionId: string, user_id: string, permissions: Permissions | null) {
  if (!user_id) throw new Error("User not authenticated");
  if (!collectionId) throw new Error("Invalid collection ID");

  //we need to assume that a usersMyCollection could be protected by permissions.  May throw an error
  try {
    const docSnap = await getDoc(doc(firebaseHelper.db, "usersMyCollection", collectionId));
    if (!docSnap.exists()) return null;
    const collectionData = docSnap.data() as CollectionInterface;
    const hasSharedAccess = hasAccessToResource(permissions, collectionId, 'read', 'collection', collectionData.uid || '', user_id);
    if (!hasSharedAccess) {
      throw new Error("You don't have permission to access this collection");
    }
    return docSnap;
  }
  catch (error) {
     throw new Error("You don't have permission to access this collection");
  }
  // const isOwner = collectionData.uid === user_id || collectionData.owner === user_id;
  // const hasSharedAccess = permissions?.collectionWrite?.includes(collectionId) || 
  //                        permissions?.collectionRead?.includes(collectionId);
  
  // if (!isOwner && !hasSharedAccess) {
  //   throw new Error("You don't have permission to access this collection");
  // }
}

export async function getCollectionsForProject(
  projectId: string, 
  user_id: string, 
  permissions: Permissions | null
): Promise<CollectionInterface[]> {
  if (!userCollectionsDB || !projectId) return [];
  
  try {
    // Query collections where associatedProjectId equals the project ID
    const q = query(
      userCollectionsDB,
      where("associatedProjectId", "==", projectId),
      orderBy("updatedAt", "desc")
    );
    
    const snapshot = await getDocs(q);
    const collections = snapshot.docs.map(doc => doc.data() as CollectionInterface);
    return collections;
  } catch (error) {
    console.error('Error fetching collections for project:', error);
    return [];
  }
}

export async function getCollectionsForProjects(
  projectIds: string[], 
  user_id: string, 
  permissions: Permissions | null
): Promise<Map<string, CollectionInterface[]>> {
  if (!userCollectionsDB || projectIds.length === 0) return new Map();
  
  try {
    const collectionsByProject = new Map<string, CollectionInterface[]>();
    
    // Initialize map with empty arrays
    projectIds.forEach(id => collectionsByProject.set(id, []));

    // Fetch collections in batches
    for (let i = 0; i < projectIds.length; i += 10) {
      const batch = projectIds.slice(i, i + 10);
      const q = query(
        userCollectionsDB,
        where("associatedProjectId", "in", batch),
        orderBy("updatedAt", "desc")
      );
      
      const snapshot = await getDocs(q);
      
      snapshot.docs.forEach(doc => {
        const collection = doc.data() as CollectionInterface;
        if (collection.associatedProjectId) {
          const projectCollections = collectionsByProject.get(collection.associatedProjectId) || [];
          projectCollections.push(collection);
          collectionsByProject.set(collection.associatedProjectId, projectCollections);
        }
      });
    }
    
    return collectionsByProject;
  } catch (error) {
    console.error('Error fetching collections for projects:', error);
    return new Map();
  }
}

// Helper function to get ALL users with write access to a project
async function getAllUsersWithProjectWriteAccess(db: any, projectId: string): Promise<string[]> {
  if(!permissionsDB) return [];
  try {
    const usersQuery = query(permissionsDB,
      where('projectWrite', 'array-contains', projectId)
    );
    const usersSnapshot = await getDocs(usersQuery);
    return usersSnapshot.docs.map(doc => doc.id);
  } catch (error) {
    console.error('Error getting users with project write access:', error);
    return [];
  }
}

// Helper function to get users with direct board access
async function getUsersWithDirectCollectionAccess(db: any, collectionId: string): Promise<string[]> {
  if(!permissionsDB) return [];
  try {
    const usersQuery = query(
      permissionsDB,
      where('collectionWrite', 'array-contains', collectionId)
    );
    const usersSnapshot = await getDocs(usersQuery);
    return usersSnapshot.docs.map(doc => doc.id);
  } catch (error) {
    console.error('Error getting users with direct board access:', error);
    return [];
  }
}


export async function getCollections(userID: string, collections: CollectionInterface[], limitRecords: number): Promise<CollectionInterface[]> {
  const queryArgs: any = [userCollectionsDB, where("uid", "==", userID), orderBy("updatedAt", "desc"), limit(limitRecords)]
  if (collections.length > 0) {
    queryArgs.splice(queryArgs.length - 1, 0, startAfter(collections[collections.length - 1].updatedAt))
  }
  const q = query.apply(this, queryArgs)
  const snapshot = await getDocs(q)
  return snapshot.docs.map(doc => doc.data() as CollectionInterface)
}

export async function updateOrSaveCollection(collection: CollectionInterface, newFileBlob: any = null): Promise<CollectionInterface> {
  try {
    validateCollection(collection)
    
    let url: any = null;
    if (newFileBlob) { 
      url = await uploadImage(collection, newFileBlob)
    }
    
    const newCollection = produce(collection, (draft: CollectionInterface) => {
      if (newFileBlob) {
        draft.coverImage = url
      }
    })
    
    const result = await updateRecord(collection.id, "usersMyCollection", newCollection);
    return newCollection as CollectionInterface;
  } catch (error) {
    console.log("error ", error); 
    throw error; // Re-throw to maintain error handling contract
  }
}

//a wrapper for async stuff
async function uploadImage(collection: CollectionInterface, blob: any) {
  return new Promise((resolve, reject) => {
    uploadFileToGCloud(`userImages/${collection.uid}`, `collection_cover_${generateID()}`, blob).then(url => resolve(url)) 
    .catch(error => reject(error));
  })
}

export async function toggleProductInCollection(
  collection: CollectionInterface,
  productId: string, 
  productThumbnail: string,
  userImage: CollectionUserImages, 
  userMaterial: CollectionUserMaterials
): Promise<CollectionInterface> {
  // This function is used to add/remove items from a collection
  // It handles products, user images, and user materials - if the item exists, it removes it; if not, it adds it
  // Used throughout the app (like in CollectionPopover) to let users manage their collection contents
  
  // Create a new collection copy with immutable updates using Immer
  const newCollection: CollectionInterface | any = produce(collection, (draft: CollectionInterface) => {
    // Initialize products array if it doesn't exist
    if (!draft.products) draft.products = []

    // Handle product toggle
    if (productId) {
      // Check if product already exists in collection
      if (draft.products.includes(productId)) {         
        // Remove product from collection
        draft.products = draft.products.filter((p: string) => p != productId) 
        // Remove corresponding thumbnail
        const t = draft.thumbnails?.filter((t: any) => t.id != productId)
        draft.thumbnails = t == null ? [] : t
      }
      else { 
        // Add product to collection
        draft.products.push(productId) 
        // Add thumbnail for the product
        if (!draft.thumbnails) draft.thumbnails = []
        draft.thumbnails?.push({id: productId, src: productThumbnail})
      }
    }
    // Handle user image toggle
    else if (userImage) {
      // Initialize userImages array if it doesn't exist
      if (!draft.userImages) draft.userImages = []
      // Check if user image already exists in collection
      if (draft.userImages.find(u => userImage.id == u.id)) {              
        // Remove user image from collection
        draft.userImages = draft.userImages.filter((u: CollectionUserImages) => u.id != userImage.id)
        // Remove corresponding thumbnail
        const t = draft.thumbnails?.filter((t: any) => t.id != userImage.id)
        draft.thumbnails = t == null ? [] : t
      }
      else {          
        // Add user image to collection
        draft.userImages.push(userImage)
        // Add thumbnail for the user image
        if (!draft.thumbnails) draft.thumbnails = []
        draft.thumbnails?.push({id: userImage.id, src: userImage.src})
      }
    }
    // Handle user material toggle
    else if (userMaterial) {
      // Initialize userMaterials array if it doesn't exist
      if (!draft.userMaterials) draft.userMaterials = []
      // Check if user material already exists in collection
      if (draft.userMaterials.find(u => userMaterial.id == u.id)) {              
        // Remove user material from collection
        draft.userMaterials = draft.userMaterials.filter((u: CollectionUserMaterials) => u.id != userMaterial.id)
        // Remove corresponding thumbnail
        const t = draft.thumbnails?.filter((t: any) => t.id != userMaterial.id)
        draft.thumbnails = t == null ? [] : t
      }
      else {          
        // Add user material to collection
        draft.userMaterials.push(userMaterial)
        // Add thumbnail for the user material
        if (!draft.thumbnails) draft.thumbnails = []
        draft.thumbnails?.push({id: userMaterial.id, src: userMaterial.src})
      }
    }
  })
  
  // Save the updated collection to the database and return it
  return await updateOrSaveCollection(newCollection)
}